import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import useIsInQualiExperiment from '~/hooks/srp/useIsInQualiExperiment';
import { BrowserCookies } from '~/utilities';
import {
  loadItemFromLocalStorage,
  saveItemToLocalStorage
} from '~/utilities/localStorage';

export const QUALI_SRP_HELIOS = 'quali-srp-helios';
const SRP_HELIOS_QUALI_CRITERIA_KEY = 'srp/srpHeliosQualiCriteria';

export type SRPHeliosQualiAction =
  | 'filterMenuInteractedWith'
  | 'filterRemoved'
  | 'suggestedFilterClicked';

export type SRPHeliosQualiCriteria = {
  filterMenuInteractedWith: boolean;
  filterRemoved: boolean;
  lastUpdated: string;
  suggestedFilterClicked: boolean;
};

type ContextType = {
  heliosQuali: {
    captureQualiAction: (action: SRPHeliosQualiAction) => void;
    lastQualiAction: SRPHeliosQualiAction | null;
    qualiCriteria: SRPHeliosQualiCriteria;
  };
};

const defaultContext: ContextType = {
  heliosQuali: {
    captureQualiAction() {},
    lastQualiAction: null,
    qualiCriteria: {
      filterMenuInteractedWith: false,
      filterRemoved: false,
      lastUpdated: new Date().toISOString(),
      suggestedFilterClicked: false
    }
  }
};

const QualiContext = createContext<ContextType>(defaultContext);

export function QualiContextProvider({
  children
}: React.PropsWithChildren<{}>) {
  const isInHeliosQualiExperiment = useIsInQualiExperiment();

  const [lastQualiAction, setLastQualiAction] =
    useState<SRPHeliosQualiAction | null>(null);
  const [qualiCriteria, setQualiCriteria] = useState<SRPHeliosQualiCriteria>(
    () => {
      const stored = loadItemFromLocalStorage<SRPHeliosQualiCriteria>(
        SRP_HELIOS_QUALI_CRITERIA_KEY
      );

      return stored || defaultContext.heliosQuali.qualiCriteria;
    }
  );

  const hasBeenAsked = useMemo(
    () => BrowserCookies.get(`${QUALI_SRP_HELIOS}-asked`) === 'asked',
    []
  );

  const captureQualiAction = useCallback(
    (action: SRPHeliosQualiAction) => {
      const shouldCapture = isInHeliosQualiExperiment && !hasBeenAsked;

      if (!shouldCapture) return;

      setLastQualiAction(action);

      if (
        qualiCriteria.filterMenuInteractedWith &&
        qualiCriteria.filterRemoved &&
        qualiCriteria.suggestedFilterClicked
      ) {
        return;
      }

      if (qualiCriteria[action]) {
        return;
      }

      const updatedCriteria = {
        ...qualiCriteria,
        [action]: true,
        lastUpdated: new Date().toISOString()
      };

      setQualiCriteria(updatedCriteria);
      saveItemToLocalStorage(SRP_HELIOS_QUALI_CRITERIA_KEY, updatedCriteria);
    },
    [hasBeenAsked, isInHeliosQualiExperiment, qualiCriteria]
  );

  useEffect(() => {
    // Reset the helios quali criteria if survey has opened
    if (!hasBeenAsked) return;

    setQualiCriteria(defaultContext.heliosQuali.qualiCriteria);

    saveItemToLocalStorage(
      SRP_HELIOS_QUALI_CRITERIA_KEY,
      defaultContext.heliosQuali.qualiCriteria
    );
  }, [hasBeenAsked]);

  const value = useMemo<ContextType>(
    () => ({
      heliosQuali: {
        captureQualiAction,
        lastQualiAction,
        qualiCriteria
      }
    }),
    [captureQualiAction, lastQualiAction, qualiCriteria]
  );

  return (
    <QualiContext.Provider value={value}>{children}</QualiContext.Provider>
  );
}

QualiContextProvider.displayName = 'QualiContextProvider';

export default QualiContext;

export function useHeliosQuali() {
  return useContext(QualiContext).heliosQuali;
}
